import { Circle, GoogleMap, Polygon, Polyline, StandaloneSearchBox } from '@react-google-maps/api';
import {
  Button,
  constants,
  Label,
  postGISToGoogleMapsPoints,
  vars,
} from '@trucktrax/trucktrax-common';
import { LocationDto } from '@trucktrax/trucktrax-ts-common';
import cx from 'classnames';
import { isEqual } from 'lodash';
import React, { Component } from 'react';

import { ADMIN_LABELS, GEOZONE_SHAPE_STYLE, GEOZONE_TYPE } from '../../../constants/appConstants';
import { CARD_MAP_HEIGHT } from '../../../constants/mapConstants';
import { ZoneType } from '../../../types';
import {
  GeocoderResponseWithStatus,
  getAddressGeocode,
  removeAutocomplete,
} from '../../../util/adminUtil';
import { noop } from '../../../util/appUtil';
import recordLog, { LogLevel } from '../../../util/errorUtil';
import {
  fromLatLngLiteral,
  isLocationValid,
  mapsToLocationDto,
  toLatLngLiteral,
} from '../../../util/geoUtil';
import { getDirections } from '../../../services/mapviewsService';
import { createIconElement } from '../../geotrax/map/markers/iconUtils';
import { CustomDrawingManager, useAdvancedMarker } from '../../geotrax/map/TraxMapDrawingManager';
import styles from './CardMap.module.css';

// Default map center (geographic center of contiguous US) to avoid showing ocean at (0,0)
const DEFAULT_MAP_CENTER: google.maps.LatLngLiteral = { lat: 39.8283, lng: -98.5795 };

// icons import removed as it was introduced in main but currently unused
interface MapChangeEvent {
  location?: LocationDto;
  circle?: {
    center: LocationDto;
    radius: number;
  };
  polygon?: {
    points: LocationDto[];
  };
}

interface OverlayCircle {
  center: LocationDto;
  radius: number;
  zoneType?: ZoneType;
}

interface PredictiveRoute {
  origin: LocationDto;
  destination: LocationDto;
}

type ShapeStyles = {
  fillColor: string;
  strokeColor: string;
  fillOpacity: number;
  strokeWeight: number;
  clickable: boolean;
  zIndex: number;
  draggable: boolean;
  editable: boolean;
};

interface MapState {
  location?: LocationDto;
  isMapHasBeenBounded: boolean;
  map?: google.maps.Map;
  predictiveRoutePath?: google.maps.LatLngLiteral[];
}

// Advanced Marker wrapper for location marker
interface LocationMarkerProps {
  map?: google.maps.Map;
  location?: LocationDto;
  showMarker?: boolean;
  isEditable?: boolean;
  onLocationChange?: (location: LocationDto) => void;
}

const LocationMarker: React.FC<LocationMarkerProps> = ({
  map,
  location,
  showMarker,
  isEditable,
  onLocationChange,
}) => {
  // Create custom flag marker content
  const flagContent = React.useMemo(() => {
    const div = document.createElement('div');
    const flagConfig = {
      path: 'M5 30 L5 5 L20 5 L15 10 L20 15 L5 15 Z',
      fillColor: vars.gray700,
      strokeColor: '#000',
      strokeWeight: 2,
      scale: 1,
    };
    const iconElement = createIconElement(flagConfig, 1);
    if (iconElement) {
      iconElement.style.width = '24px';
      iconElement.style.height = '30px';
      (iconElement as SVGElement).setAttribute?.('viewBox', '0 0 24 30');
      (iconElement as SVGElement)
        .querySelector?.('path')
        ?.setAttribute('shape-rendering', 'geometricPrecision');
      div.appendChild(iconElement);
    }
    return div;
  }, []);

  const position =
    location && location.latitude !== 0 && location.longitude !== 0
      ? ({ lat: location.latitude, lng: location.longitude } as google.maps.LatLngLiteral)
      : undefined;

  useAdvancedMarker({
    map,
    position: position || { lat: 0, lng: 0 },
    content: flagContent,
    visible: showMarker && !!position,
    draggable: isEditable,
    onDragEnd: newPosition => {
      if (onLocationChange) {
        onLocationChange({
          latitude: newPosition.lat,
          longitude: newPosition.lng,
        });
      }
    },
  });

  return null;
};

export class Map extends Component<MapProps, MapState> {
  static defaultProps: Partial<MapProps> = {
    showSearchBox: true,
    showDrawingManager: true,
    isEditable: false,
    onChange: noop,
    editMarkerOnly: false,
    defaultMapZoom: 15,
    showMarker: false,
  };

  constructor(props: MapProps) {
    super(props);

    this.state = {
      location: props.location,
      isMapHasBeenBounded: false,
      map: undefined,
      predictiveRoutePath: undefined,
    };
  }

  circleRef?: google.maps.Circle;

  overlayCircleRef?: google.maps.Circle;

  polygonRef?: google.maps.Polygon;

  mapRef?: google.maps.Map;

  searchBoxRef?: google.maps.places.SearchBox;

  listenersRef?: google.maps.MapsEventListener[];

  predictiveRouteRequestId = 0;

  componentDidMount() {
    if (this.props.address && !this.isValidLocation()) {
      // gets a location using the Geocoding API
      // https://developers.google.com/maps/documentation/geocoding/intro
      // should only get called here once if an address prop is given
      getAddressGeocode(this.props.address, this.onGeocodeSuccess);
    } else {
      this.onChange({
        location: this.props.location,
      });
    }

    removeAutocomplete();

    this.refreshPredictiveRoute();
  }

  componentDidUpdate(prevProps: MapProps) {
    const { location } = this.props;

    // Only update state.location from props if the props location is valid
    // This prevents overwriting geocoded locations with null/invalid props
    if (location !== this.state.location && isLocationValid(location)) {
      this.setState({ location });
    }

    if (!isEqual(prevProps.predictiveRoute, this.props.predictiveRoute)) {
      this.refreshPredictiveRoute();
    }
  }

  componentWillUnmount() {
    this.circleRef = undefined;
    this.overlayCircleRef = undefined;
    this.polygonRef = undefined;
    this.predictiveRouteRequestId += 1;
  }

  isValidLocation = () => isLocationValid(this.state.location);

  // Custom drawing handlers
  onMarkerPlaced = (position: google.maps.LatLngLiteral) => {
    this.onChange({
      location: fromLatLngLiteral(position),
    });
  };

  onCircleDrawingComplete = (circleData: { center: google.maps.LatLngLiteral; radius: number }) => {
    this.onChange({
      circle: {
        center: fromLatLngLiteral(circleData.center),
        radius: circleData.radius,
      },
      polygon: undefined,
    });
  };

  onPolygonDrawingComplete = (polygonData: { points: google.maps.LatLngLiteral[] }) => {
    const points = Map.googleMapsToPostGISPoints(polygonData.points) ?? [];
    this.onChange({
      circle: undefined,
      polygon: {
        points,
      },
    });
  };

  onPolygonLoad = (polygon: google.maps.Polygon) => {
    this.polygonRef = polygon;
    if (!this.listenersRef) this.listenersRef = [];
    const path = polygon.getPath();
    this.listenersRef.forEach(lis => lis.remove());
    this.listenersRef = [
      path.addListener('set_at', this.onPolygonChanged),
      path.addListener('insert_at', this.onPolygonChanged),
      path.addListener('remove_at', this.onPolygonChanged),
    ];

    if (!this.state.isMapHasBeenBounded) {
      this.setState({ isMapHasBeenBounded: true }, () => this.zoomToBounds());
    }
  };

  zoomToBounds = () => {
    if (!this.mapRef) return;
    const bounds = new window.google.maps.LatLngBounds();

    if (this.isValidLocation()) {
      const { location } = this.state;
      const sw = new window.google.maps.LatLng(
        (location?.latitude ?? 0) - 0.001,
        (location?.longitude ?? 0) - 0.001,
      );
      const ne = new window.google.maps.LatLng(
        (location?.latitude ?? 0) + 0.001,
        (location?.longitude ?? 0) + 0.001,
      );
      bounds.extend(sw);
      bounds.extend(ne);
    }

    if (this.circleRef) {
      bounds.union(this.circleRef.getBounds()!);
    }

    if (this.overlayCircleRef) {
      bounds.union(this.overlayCircleRef.getBounds()!);
    }

    if (this.polygonRef) {
      this.polygonRef
        .getPath()
        .getArray()
        .forEach(point => {
          bounds.extend(point);
        });
    }

    if (this.state.predictiveRoutePath?.length) {
      this.state.predictiveRoutePath.forEach(point => bounds.extend(point));
    }

    this.mapRef.fitBounds(bounds);
  };

  refreshPredictiveRoute = () => {
    const route = this.props.predictiveRoute;

    if (!route || !isLocationValid(route.origin) || !isLocationValid(route.destination)) {
      if (this.state.predictiveRoutePath) {
        this.setState({ predictiveRoutePath: undefined });
      }
      return;
    }

    const requestId = this.predictiveRouteRequestId + 1;
    this.predictiveRouteRequestId = requestId;

    getDirections([route.origin, route.destination], (result, status) => {
      if (requestId !== this.predictiveRouteRequestId) {
        return;
      }

      const okStatus = google.maps.DirectionsStatus?.OK ?? 'OK';
      if (status !== okStatus || !result?.routes?.length) {
        this.setState({ predictiveRoutePath: undefined });
        return;
      }

      const routePath =
        result.routes[0].overview_path?.map(point => ({ lat: point.lat(), lng: point.lng() })) ??
        [];

      this.setState({ predictiveRoutePath: routePath.length ? routePath : undefined }, () => {
        if (!this.state.isMapHasBeenBounded) {
          this.setState({ isMapHasBeenBounded: true }, () => this.zoomToBounds());
        }
      });
    });
  };

  prevChange = {};

  onChange(change: MapChangeEvent) {
    if (!this.mapRef) return;
    if (isEqual(this.prevChange, change)) {
      return;
    }

    this.prevChange = change;
    this.props.onChange!(change);
  }

  onSearchBoxUpdate = () => {
    const places = this.searchBoxRef!.getPlaces() ?? [];
    const bounds = new window.google.maps.LatLngBounds();

    places.forEach(place => {
      if (!place) {
        return;
      }

      const { location } = place.geometry!;

      if (place.geometry?.viewport) {
        bounds.union(place.geometry.viewport);
      } else {
        bounds.extend(place.geometry!.location!);
      }

      // change
      this.onChange({
        location: mapsToLocationDto(location),
      });
    });

    this.mapRef!.fitBounds(bounds);
  };

  /**
   * Processes successful geocoding results and updates map state
   */
  private handleGeocodingSuccess(place: google.maps.GeocoderResult) {
    const { location } = place.geometry;
    const convertedLocation = mapsToLocationDto(location);

    // Update state immediately so zoomToBounds uses the correct location
    this.setState({ location: convertedLocation }, () => {
      // Call onChange to notify parent
      this.onChange({
        location: convertedLocation,
      });
      // Zoom after state is updated
      this.zoomToBounds();
    });
  }

  onGeocodeSuccess = (json: google.maps.GeocoderResponse) => {
    // Type assertion needed because Google's type definitions don't include status/error_message
    const response = json as GeocoderResponseWithStatus;

    // Check status first
    if (response.status !== google.maps.GeocoderStatus.OK) {
      recordLog(LogLevel.Error, 'Geocoding failed', {
        status: response.status,
        error_message: response.error_message,
        address: this.props.address,
      });
      // Keep existing location if geocoding fails
      if (this.isValidLocation()) {
        this.onChange({
          location: this.state.location,
        });
      }
      return;
    }

    const place = json.results[0];
    if (place) {
      this.handleGeocodingSuccess(place);
    } else if (this.isValidLocation()) {
      this.onChange({
        location: this.state.location,
      });
      this.zoomToBounds();
    }
  };

  onPolygonChanged = () => {
    this.onPolygonComplete(this.polygonRef!);
  };

  onPolygonComplete = (polygon: google.maps.Polygon) => {
    const newPolygon = polygon
      .getPath()
      .getArray()
      .map(point => ({
        lat: point.lat(),
        lng: point.lng(),
      }));
    const points = Map.googleMapsToPostGISPoints(newPolygon) ?? [];
    this.onChange({
      circle: undefined,
      polygon: {
        points,
      },
    });
  };

  // Clean up refs
  onPolygonUnmount = () => {
    this.listenersRef!.forEach(lis => lis.remove());
    this.listenersRef = [];
    this.polygonRef = undefined;
  };

  onCircleUnmount = () => {
    this.listenersRef = [];
    this.circleRef = undefined;
  };

  onCircleChanged = () => {
    if (!this.mapRef) return;
    this.onCircleComplete(this.circleRef!);
  };

  onCircleComplete = (circle?: google.maps.Circle) => {
    if (!this.mapRef) return;
    if (!circle) return;
    const center = mapsToLocationDto(circle.getCenter());
    this.onChange({
      circle: {
        center,
        radius: circle.getRadius(),
      },
      polygon: undefined,
    });
  };

  /**
   * Gets the map center, preferring current map center, then state location, then US default
   */
  getCenter(): google.maps.LatLngLiteral {
    // Default to current map center, to not change viewport on update
    const currentCenter = this.mapRef?.getCenter();
    if (currentCenter) {
      const lat = currentCenter.lat();
      const lng = currentCenter.lng();
      if (lat && lng) {
        return { lat, lng };
      }
    }

    // Use state location if valid
    if (isLocationValid(this.state.location)) {
      return toLatLngLiteral(this.state.location);
    }

    // Default to center of US instead of (0,0) to avoid showing the ocean
    return DEFAULT_MAP_CENTER;
  }

  onCircleLoad = (circle: google.maps.Circle) => {
    this.circleRef = circle;
    if (!this.state.isMapHasBeenBounded) {
      this.setState({ isMapHasBeenBounded: true }, () => this.zoomToBounds());
    }
  };

  onOverlayCircleLoad = (circle: google.maps.Circle) => {
    this.overlayCircleRef = circle;
    if (!this.state.isMapHasBeenBounded) {
      this.setState({ isMapHasBeenBounded: true }, () => this.zoomToBounds());
    }
  };

  onOverlayCircleUnmount = () => {
    this.overlayCircleRef = undefined;
  };

  getSearchBoxRef = (el: google.maps.places.SearchBox) => {
    this.searchBoxRef = el;
  };

  onMapLoad = (map: google.maps.Map) => {
    this.mapRef = map;
    // Update state to trigger re-render and pass map to CustomDrawingManager
    this.setState({ map });
  };

  searchBox() {
    return (
      this.props.showSearchBox &&
      this.props.isEditable && (
        <StandaloneSearchBox onLoad={this.getSearchBoxRef} onPlacesChanged={this.onSearchBoxUpdate}>
          <input
            type="text"
            placeholder="Type address for marker"
            className={cx('tt-input', styles.mapInput)}
            onFocus={removeAutocomplete}
            style={{
              width: '240px',
              height: '32px',
              margin: '5px',
              textOverflow: 'ellipses',
              position: 'absolute',
            }}
          />
        </StandaloneSearchBox>
      )
    );
  }

  static googleMapsToPostGISPoints = (points?: google.maps.LatLngLiteral[]) => {
    if (!points) return null;

    const pointsCopy = [...points];
    pointsCopy.push(points[0]);
    return pointsCopy.map(point => ({
      latitude: point.lat,
      longitude: point.lng,
    }));
  };

  polygon = (shapeStyles: ShapeStyles) =>
    this.props.polygon &&
    this.props.polygon.points &&
    !this.props.editMarkerOnly && (
      <Polygon
        path={postGISToGoogleMapsPoints(this.props.polygon.points) as any}
        onLoad={this.onPolygonLoad}
        editable
        // Event used when dragging the whole Polygon
        onDragEnd={this.onPolygonChanged}
        // Event used when manipulating and adding points
        onMouseUp={this.onPolygonChanged}
        onUnmount={this.onPolygonUnmount}
        options={shapeStyles}
      />
    );

  circle(shapeStyles: ShapeStyles) {
    return (
      this.props.circle &&
      this.props.circle.center &&
      !this.props.editMarkerOnly && (
        <Circle
          center={toLatLngLiteral(this.props.circle.center)}
          onLoad={this.onCircleLoad}
          radius={this.props.circle.radius}
          onRadiusChanged={() => this.onCircleChanged()}
          onCenterChanged={() => this.onCircleChanged()}
          onUnmount={() => this.onCircleUnmount()}
          options={shapeStyles}
        />
      )
    );
  }

  overlayCircle() {
    const { overlayCircle } = this.props;
    if (!overlayCircle) {
      return null;
    }

    return (
      <Circle
        center={toLatLngLiteral(overlayCircle.center)}
        onLoad={this.onOverlayCircleLoad}
        radius={overlayCircle.radius}
        onUnmount={this.onOverlayCircleUnmount}
        options={{
          ...this.getGeofenceStyle(overlayCircle.zoneType),
          clickable: false,
          draggable: false,
          editable: false,
        }}
      />
    );
  }

  predictiveRoute() {
    const { predictiveRoutePath } = this.state;
    if (!predictiveRoutePath?.length) {
      return null;
    }

    return (
      <Polyline
        path={predictiveRoutePath}
        options={{
          clickable: false,
          draggable: false,
          editable: false,
          geodesic: true,
          strokeColor: '#4285F4',
          strokeOpacity: 0.8,
          strokeWeight: 4,
          zIndex: 4,
        }}
      />
    );
  }

  getGeofenceStyle(zoneType?: ZoneType) {
    const shapeStyles: ShapeStyles = {
      fillColor: '#67B4F6',
      strokeColor: '#67B4F6',
      fillOpacity: 0.25,
      strokeWeight: 2,
      clickable: false,
      zIndex: 1,
      draggable: true,
      editable: this.props.isEditable ?? false,
    };

    type GeozoneType = typeof GEOZONE_SHAPE_STYLE;
    type GeozoneShapeStyle = {
      fillColor: string;
      strokeColor: string;
      zIndex: number;
    };

    const zone: { [geozoneName: string]: GeozoneShapeStyle } = {};

    zone[GEOZONE_TYPE.PLANT] = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.PLANT as keyof GeozoneType],
      zIndex: 3,
    };
    zone[GEOZONE_TYPE.INQUEUE] = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.INQUEUE as keyof GeozoneType],
      zIndex: 2,
    };
    zone[GEOZONE_TYPE.RETURN] = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.RETURN as keyof GeozoneType],
      zIndex: 1,
    };

    return zoneType ? { ...shapeStyles, ...zone[zoneType] } : shapeStyles;
  }

  render() {
    const containerStyle = {
      width: this.props.mapContainerStyle.width || '100%',
      minHeight: this.props.mapContainerStyle.minHeight,
      height: this.props.mapContainerStyle.height || CARD_MAP_HEIGHT,
    };

    if (!this.state.location) {
      return null;
    }

    const shapeStyles = this.getGeofenceStyle(this.props.geofenceAreaType);

    return (
      <GoogleMap
        zoom={this.props.defaultMapZoom}
        // use initial center from props (marker, circle and polygon should use state)
        center={this.mapRef?.getCenter()! || this.getCenter()}
        mapContainerStyle={containerStyle}
        onLoad={this.onMapLoad}
        options={{
          draggable: this.props.isEditable,
          streetViewControl: false,
          scaleControl: false,
          mapTypeControl: true,
          controlSize: constants.GM_CONTROLSIZE,
          mapId: process.env.REACT_APP_GOOGLE_MAP_ID || 'DEMO_MAP_ID',
          mapTypeControlOptions: {
            mapTypeIds: [
              window.google.maps.MapTypeId.ROADMAP,
              window.google.maps.MapTypeId.SATELLITE,
            ],
            position: window.google.maps.ControlPosition.LEFT_BOTTOM,
          },
          panControl: false,
          zoomControl: true,
          rotateControl: false,
          fullscreenControl: false,
        }}
      >
        {this.searchBox()}

        <CustomDrawingManager
          map={this.state.map}
          onMarkerComplete={this.onMarkerPlaced}
          onCircleComplete={this.onCircleDrawingComplete}
          onPolygonComplete={this.onPolygonDrawingComplete}
          isEditable={this.props.isEditable}
          showControls={this.props.showDrawingManager}
          controlPosition={
            this.props.showSearchBox
              ? window.google.maps.ControlPosition.TOP_RIGHT
              : window.google.maps.ControlPosition.TOP_CENTER
          }
        />

        <LocationMarker
          map={this.mapRef}
          location={this.state.location}
          showMarker={this.props.showMarker}
          isEditable={this.props.isEditable}
          onLocationChange={location => this.onChange({ location })}
        />

        {this.predictiveRoute()}
        {this.overlayCircle()}
        {this.circle(shapeStyles)}
        {this.polygon(shapeStyles)}
      </GoogleMap>
    );
  }
}

type MapContainerStyle = {
  height?: string;
  minHeight?: string;
  width?: string;
  maxWidth: string;
  border?: string;
};

export interface MapProps {
  circle?: {
    center: LocationDto;
    radius: number;
  };
  polygon?: {
    points: LocationDto[];
  };
  location?: LocationDto;
  mapContainerStyle: MapContainerStyle;
  address?: string;
  showSearchBox?: boolean;
  showDrawingManager?: boolean;
  isEditable?: boolean;
  onChange?: (evt: MapChangeEvent) => void;
  editMarkerOnly?: boolean;
  defaultMapZoom?: number;
  geofenceAreaType?: ZoneType;
  showMarker?: boolean;
  overlayCircle?: OverlayCircle;
  predictiveRoute?: PredictiveRoute;
}

export default function CardMap({
  className = '',
  label = '',
  value = { circle: undefined, location: undefined, polygon: undefined },
  onChange = noop,
  resetBtn = false,
  isEditable = false,
  errorMessage,
  containerStyle = {
    height: CARD_MAP_HEIGHT,
    maxWidth: '100%',
    border: `1px solid ${vars.gray200}`,
  },
  ...rest
}: CardMapProps) {
  const isEnabled = isEditable && (value.circle || value.polygon);
  return (
    <div className={cx(className, styles.map)}>
      {label && <Label htmlFor="details-map">{label}</Label>}
      {errorMessage ? <p className={styles.errorMessageMap}>{errorMessage}</p> : <></>}
      <div className={cx(className, errorMessage ? styles.redBorderMap : '')}>
        <Map
          {...value}
          onChange={onChange}
          isEditable={isEditable}
          {...rest}
          mapContainerStyle={containerStyle}
        />
      </div>
      {resetBtn && (
        <Button
          buttonClassName={cx('tt-btn-secondary', 'tt-btn-bottom', 'full-width')}
          iconClassName="icon-undo"
          name={label === ADMIN_LABELS.GEOZONE ? 'Clear Geozone' : 'Reset Geofence'}
          dataTest="reset-geofence-btn"
          disabled={!isEnabled}
          onClick={() => {
            onChange({
              circle: undefined,
              polygon: undefined,
            });
          }}
        />
      )}
    </div>
  );
}

export interface CardMapProps extends MapProps {
  className?: string;
  label?: string;
  value?: MapChangeEvent;
  onChange?: (evt: MapChangeEvent) => void;
  resetBtn?: boolean;
  containerStyle?: MapContainerStyle;
  errorMessage?: string;
}
